<template>
    <div>
        <div id="loading-overlay" v-if="loading">
            <img src="../assets/gifs/loading.gif" alt="Carregando" class="loading-gif">
        </div>
        <div class="main-content" v-show="!loading">
            <!-- Hero Section -->
            <section class="hero-section">
                <div class="hero-content">
                    <h1>Sua jornada de aprendizado <br /> começa aqui.</h1>
                    <p>Explore nosso catálogo com os melhores cursos, pacotes e materiais didáticos para o seu
                        desenvolvimento.</p>
                    <div class="hero-search">
                        <Search @filtro-atualizado="atualizarFiltro" />
                    </div>
                </div>
            </section>

            <div class="home-container">
                <!-- Como Funciona Section (Redesigned) -->
                <div class="how-it-works">
                    <h2 class="section-title">Como funciona?</h2>
                    <div class="steps-grid">
                        <div class="step-card">
                            <div class="icon-wrapper"><i class="fa-solid fa-book-open"></i></div>
                            <h3>Escolha suas disciplinas</h3>
                            <p>Navegue pelo nosso acervo e encontre os conteúdos ideais para você.</p>
                        </div>
                        <div class="step-card">
                            <div class="icon-wrapper"><i class="fa-solid fa-layer-group"></i></div>
                            <h3>Monte seu pacote</h3>
                            <p>Selecione conteúdos avulsos ou adquira o pacote completo com desconto.</p>
                        </div>
                        <div class="step-card">
                            <div class="icon-wrapper"><i class="fa-solid fa-cart-shopping"></i></div>
                            <h3>Pagamento Facilitado</h3>
                            <p>Finalize com segurança via Cartão, Pix ou Boleto.</p>
                        </div>
                        <div class="step-card">
                            <div class="icon-wrapper"><i class="fa-solid fa-award"></i></div>
                            <h3>Aprenda e Evolua</h3>
                            <p>Acesse imediatamente seus materiais e inicie seus estudos.</p>
                        </div>
                    </div>
                </div>

                <!-- Destaques Section -->
                <div class="highlights-section">
                    <h2 class="title-home"><i class="fa-solid fa-star"></i> Destaques e Lançamentos</h2>

                    <div class="course-category">
                        <CourseCategories @category-selected="handleCategorySelected"
                            @alphabetic-order-changed="handleAlphabeticOrder" />
                    </div>

                    <div class="carousel-container">
                        <button class="carousel-btn left" @click="prevSlide">
                            <i class="fa-solid fa-chevron-left"></i>
                        </button>
                        <div class="carousel-track-wrapper">
                            <div class="carousel-track" :style="carouselStyle">
                                <div v-for="pdf in displayedPdfs" :key="pdf.hash_download">
                                    <CardDiscipline :discipline="pdf" class="carousel-item" :style="{ width: itemWidth }"
                                        :hideCart="isSystemAdmin"
                                        @add-to-cart="addToCart" />
                                </div>
                            </div>
                        </div>
                        <button class="carousel-btn right" @click="nextSlide">
                            <i class="fa-solid fa-chevron-right"></i>
                        </button>
                    </div>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import request from '../router/request';
import Search from './Search.vue';
import CardDiscipline from './CardDiscipline.vue';
import CourseCategories from './CourseCategories.vue';
import { isSystemAdministrator } from "@/utils/permissions";

export default {
    name: 'AdminHomeContent',
    components: {
        Search,
        CardDiscipline,
        CourseCategories
    },
    props: {
        cartComponent: {
            type: Object,
            default: null
        }
    },
    data() {
        return {
            filtroArea: '',
            pdfs: [],
            loading: true,
            search: '',
            currentSlide: 0,
            itemsToShow: 3,
            randomPdfs: [],
            selectedCategory: null,
            isAlphabetic: true
        };
    },

    computed: {
        isSystemAdmin() {
            return isSystemAdministrator();
        },

        itemWidth() {
            return `${100 / this.itemsToShow}%`;
        },

        carouselStyle() {
            const percentage = 100 / this.itemsToShow;
            return {
                transform: `translateX(-${this.currentSlide * percentage}%)`,
                transition: 'transform 0.5s ease-in-out',
                display: 'flex',
                width: '100%'
            };
        },

        filteredPdfs() {
            if (!Array.isArray(this.pdfs)) return [];

            let filtered = this.pdfs.filter(pdf => {
                const nomeMatch = !this.search || pdf.nome_disciplina.toLowerCase().includes(this.search.toLowerCase());
                const areaMatch = !this.filtroArea || pdf.codigo_grupo == this.filtroArea;
                const categoryMatch = !this.selectedCategory || pdf.codigo_grupo == this.selectedCategory.codigo_grupo;

                return nomeMatch && areaMatch && categoryMatch;
            });

            if (this.isAlphabetic) {
                filtered.sort((a, b) => a.nome_disciplina.localeCompare(b.nome_disciplina));
            } else {
                filtered.sort((a, b) => b.nome_disciplina.localeCompare(a.nome_disciplina));
            }

            return filtered;
        },

        displayedPdfs() {
            // Se não houver filtros ativos, mostra seleção aleatória
            if (!this.search && !this.filtroArea && !this.selectedCategory) {
                return this.randomPdfs;
            }
            // Se houver filtros, mostra os resultados filtrados e ordenados
            return this.filteredPdfs.slice(0, 10);
        }
    },

    methods: {
        atualizarFiltro(filtro) {
            this.search = filtro.texto;
            this.filtroArea = filtro.area;
        },

        handleCategorySelected(category) {
            this.selectedCategory = category;
        },

        handleAlphabeticOrder(isAlphabetic) {
            this.isAlphabetic = isAlphabetic;
        },

        prevSlide() {
            const maxIndex = this.displayedPdfs.length - this.itemsToShow;
            if (this.currentSlide > 0) {
                this.currentSlide--;
            } else {
                this.currentSlide = maxIndex;
            }
        },

        nextSlide() {
            const maxIndex = this.displayedPdfs.length - this.itemsToShow;
            if (this.currentSlide < maxIndex) {
                this.currentSlide++;
            } else {
                this.currentSlide = 0;
            }
        },

        addToCart(discipline) {
            if (this.cartComponent) {
                this.cartComponent.addToCart(discipline);
            } else {
                console.error("Componente do carrinho não está disponível");
            }
        }
    },

    mounted() {
        request.post('ava/listarBiblVirtual')
            .then(response => {
                const resultado = response.data;
                let lista = [];

                if (Array.isArray(resultado)) {
                    lista = resultado;
                } else if (Array.isArray(resultado.data)) {
                    lista = resultado.data;
                }

                this.pdfs = lista;
                this.randomPdfs = [...this.pdfs]
                    .sort(() => 0.5 - Math.random())
                    .slice(0, 10);
            })
            .catch(err => {
                console.error('Erro ao carregar livros:', err);
                this.pdfs = [];
            })
            .finally(() => {
                this.loading = false;
            });
    }
};
</script>

<style scoped src="../styles/AdminHomeContent.css" />
